import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, BarChart3, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SlideconFunnelCard — horizontal bar chart of the 4-stage Slidecon onboarding
 * funnel, with conversion percentages between stages. Polls
 * `GET /api/admin/slidecon/funnel-stats` (super_admin only).
 *
 * Stages:    v70_toast_seen → info_hint_seen → explainer_opened → slideshow_clicked
 *
 * The first stage's count is the funnel's "top" — every subsequent stage's
 * conversion is shown relative to the first stage AND relative to the previous
 * stage, so the team can spot drop-off in two ways at a glance.
 */
const STAGE_META = {
  v70_toast_seen:    { label: 'Saw v70 toast',       color: 'bg-fuchsia-500' },
  info_hint_seen:    { label: 'Saw "?" hint',        color: 'bg-violet-500'  },
  explainer_opened:  { label: 'Opened explainer',    color: 'bg-purple-500'  },
  slideshow_clicked: { label: 'Clicked Slideshow',   color: 'bg-emerald-500' },
};

const ORDER = ['v70_toast_seen', 'info_hint_seen', 'explainer_opened', 'slideshow_clicked'];

// Tiny inline SVG sparkline of the last-N daily counts for a stage.
const Sparkline = ({ data = [], stroke = '#a855f7' }) => {
  if (!data || data.length === 0) return null;
  const w = 96;
  const h = 22;
  const max = Math.max(1, ...data);
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const points = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 3) - 1.5).toFixed(1)}`).join(' ');
  const last = data[data.length - 1] || 0;
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {data.length > 0 && (
        <circle
          cx={((data.length - 1) * step).toFixed(1)}
          cy={(h - (last / max) * (h - 3) - 1.5).toFixed(1)}
          r="2"
          fill={stroke}
        />
      )}
    </svg>
  );
};

const STROKE = {
  v70_toast_seen: '#d946ef',
  info_hint_seen: '#8b5cf6',
  explainer_opened: '#a855f7',
  slideshow_clicked: '#10b981',
};

const SlideconFunnelCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState({});
  const [days, setDays] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/slidecon/funnel-stats`, {
        withCredentials: true,
      });
      setStats(r.data?.stages || {});
      setSeries(r.data?.series || {});
      setDays(r.data?.days || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load funnel stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const top = stats?.[ORDER[0]] || 0;

  return (
    <Card className="border-2 border-fuchsia-300/50 dark:border-fuchsia-700/40" data-testid="slidecon-funnel-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-fuchsia-700 dark:text-fuchsia-300">
            <BarChart3 className="w-5 h-5" />
            Slidecon Onboarding Funnel
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="slidecon-funnel-refresh-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading && !stats && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading funnel…
          </div>
        )}
        {error && (
          <p className="text-red-600 italic" data-testid="slidecon-funnel-error">{error}</p>
        )}
        {stats && top === 0 && (
          <p className="text-slate-500 italic" data-testid="slidecon-funnel-empty">
            No funnel events recorded yet. Once paid users start visiting the
            Companion, stages will populate here.
          </p>
        )}
        {stats && top > 0 && (
          <div className="space-y-2" data-testid="slidecon-funnel-stages">
            {days.length > 0 && (
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                Sparklines: new users per day · last {days.length} days
              </p>
            )}
            {ORDER.map((stage, i) => {
              const count = stats[stage] || 0;
              const pctOfTop = top > 0 ? Math.round((count / top) * 100) : 0;
              const prev = i === 0 ? null : (stats[ORDER[i - 1]] || 0);
              const pctOfPrev = prev && prev > 0
                ? Math.round((count / prev) * 100)
                : null;
              const meta = STAGE_META[stage];
              return (
                <div
                  key={stage}
                  className="space-y-1"
                  data-testid={`slidecon-funnel-stage-${stage}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{meta.label}</span>
                    <div className="flex items-center gap-2">
                      {series[stage] && (
                        <Sparkline data={series[stage]} stroke={STROKE[stage]} />
                      )}
                      <span className="text-xs text-slate-500">
                        {count} {count === 1 ? 'user' : 'users'} · {pctOfTop}% of top
                        {pctOfPrev !== null && (
                          <span className="ml-1 text-fuchsia-700 dark:text-fuchsia-300">
                            ({pctOfPrev}% of previous)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${meta.color} transition-all`}
                      style={{ width: `${pctOfTop}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {stats.complete_funnel !== undefined && (
              <div
                className="pt-3 mt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs"
                data-testid="slidecon-funnel-complete"
              >
                <span className="text-slate-500 uppercase tracking-widest">
                  Full-funnel completion
                </span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                  {stats.complete_funnel} {stats.complete_funnel === 1 ? 'user' : 'users'}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SlideconFunnelCard;
